<script setup lang="ts" generic="ColumnId extends string">
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import ContentCellSkeleton from '#components/elements/dataTable/cell/ContentCellSkeleton.vue';
import { KPI_QUERY_SORT_DIRECTION } from '@carrefour-gcs/shared';
import { createColumnHelper, getCoreRowModel, useVueTable } from '@tanstack/vue-table';
import { useElementBounding } from '@vueuse/core';
import { computed, ref, useTemplateRef } from 'vue';
import ContentCell from './cell/ContentCell.vue';
import HeaderCell from './cell/HeaderCell.vue';
import type {
  DataTableCellFormattedType,
  DataTableColumnsDefinition,
  DataTableColumnsOrder,
  DataTableFixedColumns,
  DataTableLoadMoreActionType,
  DataTableRowType,
  DataTableSort
} from './dataTable.model';
import { resolvePinnedColumns } from './pinnedColumns.util';

export interface DataTableProps<ColumnId extends string> {
  columnsDefinition: DataTableColumnsDefinition<ColumnId>;
  columnsOrder: DataTableColumnsOrder<ColumnId>;
  fixedColumns: DataTableFixedColumns<ColumnId>;
  rows: DataTableRowType<ColumnId>[];
  isLoading?: boolean;
  nbLoadingRows?: number;
  loadMore?: DataTableLoadMoreActionType;
}

const props = defineProps<DataTableProps<ColumnId>>();

const emits = defineEmits<{
  loadMore: [];
}>();

const sort = defineModel<DataTableSort<ColumnId> | null>('sort', { default: null });

function sortColumn(columnId: ColumnId) {
  if (sort.value?.columnId === columnId) {
    sort.value = { columnId, direction: sort.value.direction === 'asc' ? 'desc' : 'asc' };
  } else {
    sort.value = { columnId, direction: KPI_QUERY_SORT_DIRECTION.desc };
  }
}

const columnHelper = createColumnHelper<DataTableRowType<ColumnId>>();
const columnWidths = ref<Partial<Record<ColumnId, number>>>({});
const hoveredRow = ref<string | null>(null);

const scrollContainer = useTemplateRef<HTMLDivElement>('scrollContainer');
const { width: viewportWidth } = useElementBounding(scrollContainer);

const pinnedLayout = computed(() =>
  resolvePinnedColumns({
    columnsOrder: props.columnsOrder,
    pinnableColumns: props.fixedColumns,
    columnWidths: columnWidths.value,
    viewportWidth: viewportWidth.value
  })
);

function getPinnedLeftPosition(columnId: ColumnId): number | null {
  return pinnedLayout.value.leftPositions[columnId] ?? null;
}

function onColumnWidthChanged(columnId: ColumnId, width: number) {
  columnWidths.value[columnId] = width;
}

function isSupplierRow(row: DataTableRowType<ColumnId>) {
  let hasBenchmarkData = false;

  for (const cell of Object.values(row)) {
    const dataTableCell = cell as DataTableCellFormattedType;

    if (dataTableCell.type === 'dimension' && dataTableCell.data === 'Total') {
      return false;
    }

    if (dataTableCell.type === 'kpi' && dataTableCell.variationScoreBenchmark) {
      hasBenchmarkData = true;
    }
  }

  return hasBenchmarkData;
}

const isCompetitorVisionTable = computed(() => {
  return props.columnsOrder.some((columnId) => String(columnId).startsWith('competitor_'));
});

function getRowClasses(row: DataTableRowType<ColumnId>) {
  return computed(() => {
    if (isCompetitorVisionTable.value && isSupplierRow(row)) {
      return { 'bg-[#F7F7F7] hover:bg-gray-200': true };
    }
    return { 'bg-white hover:bg-celeste': true };
  });
}

function getContentCellClass(row: { id: string; original: DataTableRowType<ColumnId> }) {
  const isHovered = hoveredRow.value === row.id;

  if (isCompetitorVisionTable.value && isSupplierRow(row.original)) {
    return isHovered ? 'bg-gray-200' : 'bg-[#F7F7F7]';
  }

  return isHovered ? 'bg-celeste' : 'bg-white';
}

const columns = computed(() => {
  return props.columnsOrder
    .map((columnId: ColumnId) =>
      props.columnsDefinition[columnId]
        ? columnHelper.accessor((row: DataTableRowType<ColumnId>) => row[columnId], {
            id: columnId
          })
        : null
    )
    .filter(Boolean);
});

const table = useVueTable<DataTableRowType<ColumnId>>({
  get data() {
    return props.rows;
  },
  get columns() {
    return columns.value;
  },
  getCoreRowModel: getCoreRowModel(),
  state: {
    get columnPinning() {
      return {
        left: pinnedLayout.value.pinnedColumns
      };
    }
  }
});
</script>

<template>
  <div class="w-full relative">
    <div ref="scrollContainer" class="pb-4 custom-datatable-scrollbar overflow-x-auto max-h-[80vh]">
      <table class="border-separate border-spacing-0">
        <thead>
          <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <template v-for="(header, headerIndex) in headerGroup.headers" :key="header.id">
              <HeaderCell
                v-if="!header.isPlaceholder"
                :column="columnsDefinition[header.column.id as ColumnId]"
                :col-span="header.colSpan"
                :sorted-by="sort?.columnId === header.column.id ? sort.direction : null"
                :is-first="headerIndex === 0"
                :is-fixed="!!header.column.getIsPinned()"
                :fixed-left-position="getPinnedLeftPosition(header.column.id as ColumnId)"
                class="bg-[#F2F2F2]"
                @sort-column="sortColumn(header.column.id as ColumnId)"
                @width-changed="(width) => onColumnWidthChanged(header.column.id as ColumnId, width)"
              />
            </template>
          </tr>
        </thead>
        <tbody @mouseleave="hoveredRow = null">
          <tr
            v-for="(row, rowIndex) in table.getRowModel().rows"
            :key="row.id"
            :class="getRowClasses(row.original).value"
            @mouseover="hoveredRow = row.id"
          >
            <ContentCell
              v-for="(cell, cellIndex) in row.getVisibleCells()"
              :key="cell.id"
              :row="row"
              :cell="cell.getValue() as DataTableCellFormattedType"
              :column-id="cell.column.id"
              :is-first-column="cellIndex === 0"
              :is-first-row="rowIndex === 0"
              :is-fixed="!!cell.column.getIsPinned()"
              :fixed-left-position="getPinnedLeftPosition(cell.column.id as ColumnId)"
              :class="getContentCellClass(row)"
            />
          </tr>
          <template v-if="isLoading">
            <tr v-for="rowIndex in nbLoadingRows ?? 1" :key="rowIndex" class="bg-white hover:bg-celeste">
              <ContentCellSkeleton
                v-for="(column, cellIndex) in table.getAllLeafColumns()"
                :key="column.id"
                :type="props.columnsDefinition[column.id as ColumnId]?.type ?? 'dimension'"
                :is-first-column="cellIndex === 0"
                :is-first-row="rowIndex === 0"
                :is-fixed="!!column.getIsPinned()"
                :fixed-left-position="getPinnedLeftPosition(column.id as ColumnId)"
                class="bg-white"
              />
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <ButtonBase
      v-if="!!loadMore?.canLoadMore"
      :disabled="loadMore.isLoading"
      :full-width="true"
      icon-right="plus-square"
      background="transparent"
      variant="secondary"
      size="large"
      class="mt-6"
      :label="loadMore.label"
      @click="emits('loadMore')"
    />
  </div>
</template>
